import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react';
import { css } from '@emotion/react';
import { pendo } from '@frontend/tracking';
import { useOnboardingShallowStore } from '../hooks/use-onboarding-store';
import { useOnboardingTargetSelector } from '../hooks/use-onboarding-target-selector';
import type { DotPosition, OffsetStyle } from '../types';
import { dotPositionStyles } from './dot-position-styles';
import { HotspotNudge } from './hotspot-nudge';
import { OnboardingTargetNudge } from './onboarding-target-nudge';

interface OnboardingHotspotProps {
  target: string;
  enabled?: boolean;
  position?: DotPosition;
  offset?: OffsetStyle;
  children: ReactNode;
  /**
   * Escape hatch for cases where the actual clickable/visual target is inside
   * the wrapped children and cannot be wrapped directly.
   *
   * The wrapper still registers the logical onboarding target, but this selector
   * becomes the actual nudge anchor and click completion boundary.
   */
  targetSelector?: string;
}

export function OnboardingHotspot({ enabled = true, children, ...props }: OnboardingHotspotProps) {
  if (!enabled) {
    return <>{children}</>;
  }

  return <OnboardingHotspotContent {...props}>{children}</OnboardingHotspotContent>;
}

function OnboardingHotspotContent({
  target,
  position = 'top-right',
  offset,
  children,
  targetSelector,
}: Omit<OnboardingHotspotProps, 'enabled'>) {
  const { mountTarget, unmountTarget, activeHotspot, activeInfoHotspots, completeHotspot } = useOnboardingShallowStore(
    'mountTarget',
    'unmountTarget',
    'activeHotspot',
    'activeInfoHotspots',
    'completeHotspot'
  );

  const instanceId = useId();
  const trackedSeenHotspotKeyRef = useRef<string | undefined>();

  const selectorTargetElement = useOnboardingTargetSelector(targetSelector);

  useEffect(() => {
    mountTarget(target, instanceId);
    return () => unmountTarget(instanceId);
  }, [target, instanceId, mountTarget, unmountTarget]);

  const infoHotspotForTarget = activeInfoHotspots.find((hotspot) => hotspot.target === target);
  const infoHotspotKey = infoHotspotForTarget?.key;

  const hasActiveGuidedHotspot = activeHotspot?.target === target && activeHotspot?.instanceId === instanceId;

  const hotspotInteractionTrackingId = hasActiveGuidedHotspot
    ? activeHotspot?.tracking?.interactionTrackingId
    : undefined;

  const handleHotspotComplete = useCallback(() => {
    if (!hasActiveGuidedHotspot || !activeHotspot) return;

    completeHotspot(activeHotspot.key);
  }, [hasActiveGuidedHotspot, activeHotspot, completeHotspot]);

  useEffect(() => {
    if (infoHotspotKey) completeHotspot(infoHotspotKey);
  }, [infoHotspotKey, completeHotspot]);

  /**
   * In selector mode, only clicks on the selected inner target should complete
   * the hotspot. We do not stop propagation, so the target's normal click
   * behavior continues to work.
   */
  useEffect(() => {
    if (!targetSelector) return;
    if (!selectorTargetElement) return;
    if (!hasActiveGuidedHotspot) return;

    selectorTargetElement.addEventListener('click', handleHotspotComplete);

    return () => {
      selectorTargetElement.removeEventListener('click', handleHotspotComplete);
    };
  }, [targetSelector, selectorTargetElement, hasActiveGuidedHotspot, handleHotspotComplete]);

  /**
   * Track "hotspot seen" when this guided hotspot becomes active.
   * This does not complete the hotspot. Completion still happens only when the user interacts with the target element.
   */
  useEffect(() => {
    if (!hasActiveGuidedHotspot || !activeHotspot) return;

    const seenEvent = activeHotspot.tracking?.seenEvent;

    if (!seenEvent) return;
    if (trackedSeenHotspotKeyRef.current === activeHotspot.key) return;

    trackedSeenHotspotKeyRef.current = activeHotspot.key;

    pendo.track(seenEvent, {
      hotspotKey: activeHotspot.key,
      target: activeHotspot.target,
      date: new Date().toLocaleString(),
    });
  }, [hasActiveGuidedHotspot, activeHotspot]);

  /**
   * Selector mode keeps the logical wrapper but renders the nudge inside the selected target.
   */
  if (targetSelector) {
    return (
      <>
        {children}

        {hasActiveGuidedHotspot && selectorTargetElement && (
          <OnboardingTargetNudge targetElement={selectorTargetElement} position={position} offset={offset} />
        )}
      </>
    );
  }

  return (
    <div css={styles.wrapper} onClick={handleHotspotComplete} data-trackingid={hotspotInteractionTrackingId}>
      {children}
      {hasActiveGuidedHotspot && (
        <HotspotNudge className='onboarding-hotspot-nudge' css={dotPositionStyles[position]} offset={offset} />
      )}
    </div>
  );
}

const styles = {
  wrapper: css({
    position: 'relative',
    display: 'inline',
  }),
};
